import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider, AuthenticatedTemplate, useMsal, UnauthenticatedTemplate } from '@azure/msal-react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { PageLayout } from './components/PageLayout';
import { Landing } from './components/Landing';
import { Projects } from './components/Projects';
import { ProjectDetail } from './components/Projects/ProjectDetail';
import { ProjectCheckDetail } from './components/Projects/ProjectDetail/ProjectCheckDetail/ProjectCheckDetail';
import { Rules } from './components/Rules';
import { Organization } from './components/Organization';
import { InvitationPage } from './components/Invitations';
import { apiRequest } from './authConfig';
import { store } from './flux/stores/store';
import { useAppDispatch } from './flux/stores/hooks';
import { fetchCurrentUser } from './flux/actions/userThunks';

import './styles/App.css';

const TokenAcquirer = () => {
    const { instance } = useMsal();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const activeAccount = instance.getActiveAccount();
    const isInvitationRoute = location.pathname.startsWith('/invitations/');
    // MSAL rebuilds a fresh AccountInfo object from cache on every
    // getActiveAccount() call — it's never the same reference twice, even for
    // the same signed-in user. Keying the effect on the whole object would
    // re-run it (and re-hit /me) on every re-render of this component, not just
    // when the signed-in account actually changes. Key on a stable primitive.
    const activeAccountKey = activeAccount?.homeAccountId ?? null;

    useEffect(() => {
        // Invitation links have their own controlled token and recovery flow.
        // Do not start a second redirect-capable token request while that page
        // is resolving an invitation.
        if (!activeAccount || isInvitationRoute) return;

        let cancelled = false;
        const request = { ...apiRequest, account: activeAccount };

        instance
            .acquireTokenSilent(request)
            .then((response) => {
                if (cancelled) return;
                dispatch(fetchCurrentUser({ accessToken: response.accessToken }));
            })
            .catch((error) => {
                if (cancelled) return;
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(request);
                    return;
                }
                console.error('Failed to acquire API token:', error);
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeAccountKey, instance, dispatch, isInvitationRoute]);

    return null;
};

const ProtectedRoute = ({ element }) => (
    <>
        <AuthenticatedTemplate>{element}</AuthenticatedTemplate>
        <UnauthenticatedTemplate>
            <Navigate to="/" replace />
        </UnauthenticatedTemplate>
    </>
);

const App = ({ instance }) => {
    return (
        <Provider store={store}>
            <MsalProvider instance={instance}>
                <BrowserRouter>
                    <PageLayout>
                        <AuthenticatedTemplate>
                            <TokenAcquirer />
                        </AuthenticatedTemplate>
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <>
                                        <AuthenticatedTemplate>
                                            <Navigate to="/projects" replace />
                                        </AuthenticatedTemplate>
                                        <UnauthenticatedTemplate>
                                            <Landing />
                                        </UnauthenticatedTemplate>
                                    </>
                                }
                            />
                            <Route path="/projects" element={<ProtectedRoute element={<Projects />} />} />
                            <Route path="/projects/:projectId" element={<ProtectedRoute element={<ProjectDetail />} />} />
                            <Route path="/projects/:projectId/checks/:jobId" element={<ProtectedRoute element={<ProjectCheckDetail />} />} />
                            <Route path="/rules" element={<ProtectedRoute element={<Rules />} />} />
                            <Route path="/organization" element={<ProtectedRoute element={<Organization />} />} />
                            <Route path="/invitations/:token" element={<InvitationPage />} />
                        </Routes>
                    </PageLayout>
                </BrowserRouter>
            </MsalProvider>
        </Provider>
    );
};

export default App;
